import { useFormik } from "formik";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { useState, useEffect, useRef } from "react";
import ComplaintService from "../../services/complaint/ComplaintService";
import { Toast } from "primereact/toast";
import { ProductDetails } from "../../models/complaints/complaint-model";
import { useNavigate } from 'react-router-dom';
import { OrderModel, OrderInvoice } from "../../models/orders/order-list-model";
import { ImageTemplateDemo } from "../images/file-upload-template";
import { MenuType } from "../../types/enum";
import { usePageNavigation } from "../user-menu/menu/navigation";

interface EditComplaintProps {
  isUpdate: boolean;
  handleOnSuccess: () => void;
  updateComplaintInfo: OrderModel | null;
}

const CustomerProdComplaintRegister: React.FC<EditComplaintProps> = ({
  isUpdate,
  handleOnSuccess,
  updateComplaintInfo
}) => {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [products, setProducts] = useState<ProductDetails[]>([{ productId: 0, name: '', quantity: 0, orderInvoiceDetailID: 0 }]);
  const [orderProduct, setOrderProduct] = useState<any[]>([]);
  const toast = useRef<Toast>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loginUser, setLoginUser] = useState<any>();
  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const navigate = useNavigate();
  const [containerType, setContainerType] = useState<string>("Complaints");
  const navigateToPage = usePageNavigation();
  const productInitialValues = {
    customerId: null as number | null,
    invoiceId: null as OrderInvoice | null,
    productId: null as number | null,
    categoryId: 0,
    description: "",
    image: null as any | null,
    containerType: null as string | null,
  };

  const productValidationSchema = Yup.object({
    customerId: Yup.number().required("Customer is required"),
    invoiceId: Yup.object().required("Invoice is required"),
    productId: Yup.number().required("Product is required"),
    description: Yup.string().required("Description is required"),
  });

  const productFormik = useFormik({
    initialValues: productInitialValues,
    validationSchema: productValidationSchema,
    onSubmit: async (data) => {
      if (isSubmitted) return;
      setIsSubmitted(true);
      const LoginDetails = localStorage.getItem('user');
      const user = LoginDetails ? JSON.parse(LoginDetails) : null;

      const complaint = {
        id: 0,
        customerId: user?.userID || updateComplaintInfo?.customerID || 0,
        invoiceId: updateComplaintInfo?.orderInvoice?.[0]?.orderInvoiceID || updateComplaintInfo?.invoiceId || 0,
        productId: updateComplaintInfo?.productID || 0,
        categoryId: updateComplaintInfo?.categoryID || 0,
        description: data.description,
        image: imageFile,
        createdBy: user?.userID || 0,
        createdDate: new Date().toISOString(),
        modifiedBy: 0,
        modifiedDate: new Date().toISOString(),
        containerType: containerType
      };

      try {
        const response = await ComplaintService.CreateCustProductComplaint(complaint);
        if (response.isSuccess) {
          showMessage('Success', 'Product complaint created successfully', 'success');
          handleOnSuccess();
          navigateToPage(MenuType.ViewAllComplaints, { state: { complaintType: 'product' } });
        } else {
          showMessage('Error', response.message, 'error');
        }
      } catch (error) {
        console.error("Error during product complaint submission", error);
        showMessage('Error', 'Failed to create product complaint', 'error');
      } finally {
        setIsSubmitted(false);
      }
    },
  });

  useEffect(() => {
    if (updateComplaintInfo) {
      const orderCategory = updateComplaintInfo.orderDetails?.[0]?.categoryID || updateComplaintInfo.categoryID || 0;

      productFormik.setValues({
        customerId: updateComplaintInfo.customerID || 0,
        invoiceId: updateComplaintInfo.orderInvoice?.[0] || {
          orderInvoiceID: updateComplaintInfo.orderID,
          invoiceNo: updateComplaintInfo.invoiceNo?.toString() || '',
          invoiceDate: updateComplaintInfo.invoicedDate || ''
        },
        productId: updateComplaintInfo.productID || 0,
        categoryId: orderCategory,
        description: '',
        image: null,
        containerType: containerType
      });

      const LoginDetails = localStorage.getItem('user');
      if (LoginDetails) {
        const user = JSON.parse(LoginDetails);
        setLoginUser(user);
      }
    }
  }, [updateComplaintInfo, containerType]);

  useEffect(() => {
    if (products && products.length > 0) {
      setOrderProduct(products.map((product) => ({
        productId: product.productId,
        detail: product.productId !== 0 ? `${product.name}, Quantity - ${product.quantity}` : "",
      })));
    }
  }, [products]);

  const showMessage = (label: string, message: string, severity: 'success' | 'error' | 'info' | 'warn') => {
    toast.current?.show({ severity, summary: label, detail: message, life: 3000 });
  };

  const fieldHoverStyle = (fieldName: string) => ({
    border: hoveredField === fieldName ? '2px solid #6366F1' : '1px solid #ced4da',
    borderRadius: '6px',
    transition: 'border 0.2s ease-in-out',
    width: '100%',
    minHeight: '40px'
  });

  const handleFileUpload = (file: any) => {
    setImageFile(file);
    productFormik.setFieldValue("image", file);
  };

  const handleFileSizeExceeded = (message: string) => {
    showMessage('Info', message, 'info');
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <div className="p-3">
        <div className="flex justify-content-center">
          <div className="w-full md:w-10 lg:w-8">
            <div className="card p-0 border-round w-full">
              <div className="p-3">
                <h5 className="border-round mb-3 order-top-color p-2 text-center shadow-3">
                  Product Complaint Registration
                </h5>

                <form onSubmit={productFormik.handleSubmit} className="w-full">
                  <div className="formgrid grid">

                    {/* Invoice # */}
                    <div className="field col-12 md:col-6">
                      <label htmlFor="invoiceDisplay" className="font-bold">Invoice #</label>
                      <InputText
                        id="invoiceDisplay"
                        value={updateComplaintInfo?.invoiceNo?.toString() ||
                          updateComplaintInfo?.orderInvoice?.[0]?.invoiceNo?.toString() || ''}
                        readOnly
                        className="w-full"
                      />
                    </div>

                    {/* Product Name */}
                    <div className="field col-12 md:col-6">
                      <label htmlFor="productDisplay" className="font-bold">Product</label>
                      <InputText
                        id="productDisplay"
                        value={updateComplaintInfo?.productName ||
                          updateComplaintInfo?.orderDetails?.[0]?.productName || ''}
                        readOnly
                        className="w-full"
                      />
                    </div>

                    {/* Description */}
                    <div className="field col-12">
                      <label htmlFor="description" className="font-bold">
                        Description<span className="text-red-500">*</span>
                      </label>
                      <div
                        onMouseEnter={() => setHoveredField('description')}
                        onMouseLeave={() => setHoveredField(null)}
                        style={fieldHoverStyle('description')}
                      >
                        <InputTextarea
                          placeholder="Enter Description"
                          name="description"
                          id="description"
                          rows={5}
                          className="w-full border-none outline-none p-3 text-base"
                          value={productFormik.values.description}
                          onChange={(e) => {
                            const cleanedValue = e.target.value.replace(/\s\s+/g, ' ');
                            productFormik.setFieldValue('description', cleanedValue);
                          }}
                        />
                      </div>
                      {productFormik.errors.description && productFormik.touched.description && (
                        <small className="p-error">{productFormik.errors.description as string}</small>
                      )}
                    </div>
                    {/* Image Upload */}
                    <ImageTemplateDemo onFileUpload={handleFileUpload} handelFileSize={handleFileSizeExceeded} />
                    {/* Buttons */}
                    <div className="field col-12 flex flex-column sm:flex-row justify-content-end gap-3">
                      <Button
                        type="button"
                        label="Reset"
                        className="p-button-secondary w-full sm:w-auto"
                        onClick={() => {
                          productFormik.resetForm();
                          setImageFile(null);
                        }}
                      />
                      <Button
                        type="submit"
                        label={isSubmitted ? "Submitting..." : "Save Complaint"}
                        className="p-button-primary w-full sm:w-auto"
                        disabled={isSubmitted}
                      />
                    </div>

                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomerProdComplaintRegister;
